import { RequestError } from "./request-error";
import { withRequestDeadline } from "./request-signal";

// Shared HTTP transport for server and browser callers. Keep it free of client directives.

/** Thrown by every read here, so a caller can narrow on the status it expects
 * (e.g. suppress an expected 403 without swallowing 5xx/network failures). */
export class ApiError extends RequestError {
  constructor(
    message: string,
    readonly status: number,
    readonly kind?: string,
    readonly detail?: unknown,
    response?: Response,
  ) {
    super(message, response);
    this.name = "ApiError";
  }
}

export function requestUnavailable(error: Error | null): boolean {
  return (
    error instanceof ApiError && [401, 403, 404, 410].includes(error.status)
  );
}

// What a caller that must work in both graphs asks for instead of importing a
// concrete `apiGet`. Two verbs, because that is all the read paths use.
export type ApiTransport = {
  get<T>(path: string): Promise<T>;
  post<T>(path: string, body?: unknown): Promise<T>;
};

export async function parseResponse<T>(response: Response): Promise<T> {
  if (response.ok) {
    if (response.status === 204) return undefined as T;

    return response.json() as Promise<T>;
  }

  let detail = `${response.status} ${response.statusText}`;
  let kind: string | undefined;
  let structuredDetail: unknown;

  try {
    const body = await response.json();
    structuredDetail = body.detail;

    if (typeof body.detail === "string") {
      detail = body.detail;
    } else if (
      typeof body.detail === "object" &&
      body.detail !== null &&
      typeof body.detail.message === "string"
    ) {
      detail = body.detail.message;
      kind =
        typeof body.detail.kind === "string" ? body.detail.kind : undefined;
    } else if (Array.isArray(body.detail)) {
      detail = body.detail
        .map((entry: unknown) =>
          typeof entry === "object" &&
          entry !== null &&
          "msg" in entry &&
          typeof entry.msg === "string"
            ? entry.msg
            : JSON.stringify(entry),
        )
        .join(", ");
    } else if (typeof body.error === "string") {
      detail = body.error;
    }
  } catch {
    // Ignore JSON parse failures and fall back to the HTTP status line.
  }

  throw new ApiError(detail, response.status, kind, structuredDetail, response);
}

export const DEFAULT_BACKEND_FETCH_TIMEOUT_MS = 8000;

export type BackendRequestInit = RequestInit & {
  /** Null delegates the request lifetime to its caller's signal. */
  timeoutMs?: number | null;
};

export function backendFetchResponse(
  base: string,
  path: string,
  { timeoutMs = DEFAULT_BACKEND_FETCH_TIMEOUT_MS, ...init }: BackendRequestInit,
  fetchRequest: typeof fetch = fetch,
): Promise<Response> {
  const request: RequestInit = {
    cache: "no-store",
    ...init,
  };

  if (timeoutMs !== null) {
    request.signal = withRequestDeadline(init.signal ?? undefined, timeoutMs);
  }

  return fetchRequest(`${base}${path}`, request);
}

export async function backendFetch<T>(
  base: string,
  path: string,
  init: BackendRequestInit,
): Promise<T> {
  const response = await backendFetchResponse(base, path, init);
  return parseResponse<T>(response);
}
